import { useCallback, useEffect, useMemo, useState } from "react";

import { Audit } from "../../../api";

import styles from "./AuditPage.module.scss";
import {
  AUDIT_ACTION_OPTIONS,
  AUDIT_MENU_OPTIONS,
  LIMIT_OPTIONS,
  formatChangeSummary,
  formatDateTime,
  getActionBadgeClass,
  getActionLabel,
  getAuditLogsResponse,
  getAuditUserName,
  getMenuLabel,
} from "./AuditPage.form";

export function AuditPage() {
  const [logs, setLogs] = useState([]);

  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 20,
  });

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [menuFilter, setMenuFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");

  const [limit, setLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const auditController = new Audit();

      const result = await auditController.getLogs(currentPage, limit, {
        menuKey: menuFilter || undefined,
        action: actionFilter || undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
        search: appliedSearch || undefined,
      });

      const response = getAuditLogsResponse(result);

      setLogs(response.docs || []);

      setPagination({
        totalDocs: Number(response.totalDocs) || 0,
        totalPages: Math.max(Number(response.totalPages) || 1, 1),
        page: Number(response.page) || currentPage,
        limit: Number(response.limit) || limit,
      });
    } catch (errorResponse) {
      console.error("loadLogs error:", errorResponse);

      setLogs([]);

      setPagination({
        totalDocs: 0,
        totalPages: 1,
        page: 1,
        limit,
      });

      setError(
        errorResponse?.msg ||
          "No fue posible cargar la bitácora. Intenta nuevamente."
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    appliedSearch,
    actionFilter,
    currentPage,
    dateFromFilter,
    dateToFilter,
    limit,
    menuFilter,
  ]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const firstVisibleItem = useMemo(() => {
    if (pagination.totalDocs === 0) {
      return 0;
    }

    return (pagination.page - 1) * pagination.limit + 1;
  }, [pagination]);

  const lastVisibleItem = useMemo(() => {
    return Math.min(pagination.page * pagination.limit, pagination.totalDocs);
  }, [pagination]);

  const hasFilters = Boolean(
    appliedSearch || menuFilter || actionFilter || dateFromFilter || dateToFilter
  );

  function handleSearchSubmit(event) {
    event.preventDefault();
    setCurrentPage(1);
    setAppliedSearch(searchValue.trim());
  }

  function handleMenuFilterChange(event) {
    setMenuFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleActionFilterChange(event) {
    setActionFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleDateFromChange(event) {
    setDateFromFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleDateToChange(event) {
    setDateToFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleClearFilters() {
    setSearchValue("");
    setAppliedSearch("");
    setMenuFilter("");
    setActionFilter("");
    setDateFromFilter("");
    setDateToFilter("");
    setCurrentPage(1);
  }

  function handleLimitChange(event) {
    setLimit(Number(event.target.value));
    setCurrentPage(1);
  }

  return (
    <section className={styles.AuditPage}>
      <header className={styles.PageHeader}>
        <div>
          <p className={styles.Breadcrumb}>Administración / Bitácora</p>

          <h1>Bitácora del sistema</h1>

          <p className={styles.Subtitle}>
            Historial de cambios en productos, categorías, usuarios, roles y
            demás módulos, con quién hizo cada cambio y cuándo.
          </p>
        </div>
      </header>

      <section className={styles.AuditCard}>
        <div className={styles.FiltersBar}>
          <form className={styles.SearchForm} onSubmit={handleSearchSubmit}>
            <input
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Buscar por usuario o entidad..."
              aria-label="Buscar por usuario o entidad"
            />

            <button type="submit" aria-label="Buscar">
              ⌕
            </button>
          </form>

          <label className={styles.FilterControl}>
            <span>Módulo</span>

            <select value={menuFilter} onChange={handleMenuFilterChange}>
              <option value="">Todos</option>

              {AUDIT_MENU_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.FilterControl}>
            <span>Acción</span>

            <select value={actionFilter} onChange={handleActionFilterChange}>
              <option value="">Todas</option>

              {AUDIT_ACTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className={styles.DateRangeControl}>
            <span>Rango de fechas</span>

            <div className={styles.DateRangeInputs}>
              <input
                type="date"
                value={dateFromFilter}
                onChange={handleDateFromChange}
                aria-label="Desde"
              />

              <span>—</span>

              <input
                type="date"
                value={dateToFilter}
                onChange={handleDateToChange}
                aria-label="Hasta"
              />
            </div>
          </div>

          <button
            type="button"
            className={styles.RefreshButton}
            onClick={loadLogs}
            disabled={isLoading}
          >
            {isLoading ? "Actualizando..." : "Actualizar"}
          </button>

          {hasFilters && (
            <button
              type="button"
              className={styles.ClearFiltersButton}
              onClick={handleClearFilters}
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {error && (
          <div className={styles.ErrorMessage} role="alert">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className={styles.StateMessage}>Cargando bitácora...</div>
        ) : logs.length === 0 ? (
          <div className={styles.EmptyState}>
            <p>
              {hasFilters
                ? "No se encontraron registros con los filtros seleccionados."
                : "Todavía no hay cambios registrados en la bitácora."}
            </p>

            {hasFilters && (
              <button
                type="button"
                className={styles.ClearFiltersButton}
                onClick={handleClearFilters}
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.TableContainer}>
              <table className={styles.Table}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Módulo</th>
                    <th>Entidad</th>
                    <th>Acción</th>
                    <th>Cambios</th>
                  </tr>
                </thead>

                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td>
                        <span className={styles.DateCell}>
                          {formatDateTime(log.createdAt)}
                        </span>
                      </td>

                      <td>
                        <span
                          className={styles.UserValue}
                          title={getAuditUserName(log)}
                        >
                          {getAuditUserName(log)}
                        </span>
                      </td>

                      <td>
                        <span className={styles.MenuValue}>
                          {getMenuLabel(log.menuKey)}
                        </span>
                      </td>

                      <td>
                        <span
                          className={styles.EntityValue}
                          title={log.entityLabel || ""}
                        >
                          {log.entityLabel || "—"}
                        </span>
                      </td>

                      <td>
                        <span
                          className={getActionBadgeClass(styles, log.action)}
                        >
                          {getActionLabel(log.action)}
                        </span>
                      </td>

                      <td>
                        {Array.isArray(log.changes) && log.changes.length > 0 ? (
                          <ul className={styles.ChangesList}>
                            {log.changes.map((change, index) => (
                              <li key={`${log._id}-${index}`}>
                                {formatChangeSummary(change)}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className={styles.NoChanges}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <footer className={styles.Pagination}>
              <span className={styles.TableInfo}>
                Mostrando {firstVisibleItem} a {lastVisibleItem} de{" "}
                {pagination.totalDocs} registros
              </span>

              <div className={styles.PageControls}>
                <button
                  type="button"
                  className={styles.PageButton}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(page - 1, 1))
                  }
                  disabled={currentPage === 1}
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                <span className={styles.CurrentPage}>
                  {pagination.page} de {pagination.totalPages}
                </span>

                <button
                  type="button"
                  className={styles.PageButton}
                  onClick={() =>
                    setCurrentPage((page) =>
                      Math.min(page + 1, pagination.totalPages)
                    )
                  }
                  disabled={currentPage === pagination.totalPages}
                  aria-label="Página siguiente"
                >
                  ›
                </button>
              </div>

              <label className={styles.LimitControl}>
                <select value={limit} onChange={handleLimitChange}>
                  {LIMIT_OPTIONS.map((optionLimit) => (
                    <option key={optionLimit} value={optionLimit}>
                      {optionLimit} por página
                    </option>
                  ))}
                </select>
              </label>
            </footer>
          </>
        )}
      </section>
    </section>
  );
}
